import type { RoutesNamesList } from '@typed-router';

export const useTermsStore = defineStore('terms', () => {
  const terms = ref<Term[]>([]);
  const storedTermsMenu = ref<Term[]>([]);
  const termsLinks = ref<TermsLink[]>([]);

  const fetchTerms = async () => {
    try {
      const { ApiService } = useApiService();

      const {
        data: { data },
      } = await ApiService.get('/getlegalterm');

      terms.value = data;
    } catch (err) {
      throw new Error(`${err}`);
    }
  };

  const getCurrentLangTermsPlusDefaultTerms = async (lang: string) => {
    let resp: Term[] = [];

    if (!terms.value.length) {
      await fetchTerms();
    }

    if (!terms.value.length) {
      resp = [...fallbackMenu];
    } else {
      const uniqueRoutes = new Set<string>();
      const allItems = [...terms.value, ...fallbackMenu];

      allItems.forEach((item) => {
        uniqueRoutes.add(item.route);
      });

      uniqueRoutes.forEach((uniqueRoute) => {
        const currentLangTerm = terms.value.find(
          (item) => item.route === uniqueRoute && item.language === lang,
        );

        const termToAdd =
          currentLangTerm ||
          terms.value.find((item) => item.route === uniqueRoute) ||
          fallbackMenu.find((item) => item.route === uniqueRoute);

        if (termToAdd) {
          resp.push(termToAdd);
        }
      });
    }

    storedTermsMenu.value = [...resp];

    const { linkToResponsibleGaming } = useLinks();

    let mapTerms = storedTermsMenu.value.map(({ language, title, route }) => ({
      language,
      title,
      route: '/page/' + route,
    }));

    if (linkToResponsibleGaming.value) {
      mapTerms = mapTerms.filter(
        (item) => item.route !== '/page/responsible-gaming',
      );
    }

    termsLinks.value = mapTerms;

    return resp;
  };

  const { isUserMigrationEnabled } = useFeatureFlagsNew();

  const getLegalTerm = async (routeName: RoutesNamesList, lang: string) => {
    if (!storedTermsMenu.value.length) {
      await getCurrentLangTermsPlusDefaultTerms(lang);
    }

    return storedTermsMenu.value.find((item) => item.route === routeName);
  };

  const fallbackMenu = [
    {
      language: 'en',
      title: 'terms.betting_terms',
      route: 'betting-terms',
      id: 0,
      mainTerm: true,
      description: '',
      created_at: null,
      updated_at: undefined,
      deleted_at: null,
      term_file: undefined,
    },
    ...(!isUserMigrationEnabled.value
      ? []
      : [
          {
            language: 'en',
            title: 'terms.consent',
            route: 'consent',
            id: 0,
            mainTerm: false,
            description: '',
            created_at: null,
            updated_at: undefined,
            deleted_at: null,
            term_file: undefined,
          },
        ]),
    {
      language: 'en',
      title: 'terms.terms_and_conditions',
      route: 'terms',
      id: 0,
      mainTerm: false,
      description: '',
      created_at: null,
      updated_at: undefined,
      deleted_at: null,
      term_file: undefined,
    },
    {
      language: 'en',
      title: 'terms.privacy_policy',
      route: 'privacy',
      id: 0,
      mainTerm: false,
      description: '',
      created_at: null,
      updated_at: undefined,
      deleted_at: null,
      term_file: undefined,
    },
    {
      language: 'pt-br',
      title: 'terms.kyc-policy',
      route: 'kyc-policy',
      id: 0,
      mainTerm: false,
      description: '',
      created_at: null,
      updated_at: undefined,
      deleted_at: null,
      term_file: undefined,
    },
    {
      language: 'pt-br',
      title: 'terms.pld-ftp-policy',
      route: 'pld-ftp-policy',
      id: 0,
      mainTerm: false,
      description: '',
      created_at: null,
      updated_at: undefined,
      deleted_at: null,
      term_file: undefined,
    },
    {
      language: 'pt-br',
      title: 'terms.lgpd',
      route: 'lgpd',
      id: 0,
      mainTerm: false,
      description: '',
      created_at: null,
      updated_at: undefined,
      deleted_at: null,
      term_file: undefined,
    },
    {
      language: 'en',
      title: 'terms.responsible_gaming',
      route: 'responsible-gaming',
      id: 0,
      mainTerm: false,
      description: '',
      created_at: null,
      updated_at: undefined,
      deleted_at: null,
      term_file: undefined,
    },
  ];

  return {
    terms,
    storedTermsMenu,
    termsLinks,
    fetchTerms,
    getCurrentLangTermsPlusDefaultTerms,
    getLegalTerm,
  };
});
